import { css } from '@emotion/core'

import {
  insightsTestimonials,
  portalTestimonials,
  prioritizationTestimonials,
  roadmapsTestimonials,
} from './testimonials'

export const FEATURES = [
  // roadmaps
  {
    slug: 'roadmaps',
    metaDescription: {
      en: `Build strategic and lean roadmaps with airfocus's product roadmap software. Focus on outcomes, escape the feature factory, and share tailored roadmaps.`,
      fr: "Élaborez des feuilles de route stratégiques et agiles avec le logiciel de feuille de route produit d'airfocus. Mettez l'accent sur les résultats, échappez à la production de fonctionnalités et partagez des feuilles de route personnalisées.",
      de: 'Erstelle strategische und schlanke Roadmaps mit der Produkt-Roadmap-Software von airfocus. Fokussiere Dich auf Ergebnisse, entkomme der Feature-Hölle und teile maßgeschneiderte Roadmaps.',
    },
    subheading: { en: 'Product Roadmap Software', fr: 'Logiciel de roadmaps produit', de: 'Product Roadmap Software' },
    heading: {
      en: "Align everyone around <br class='hide-on-small-medium-large'/> the vision with clear <br class='hide-on-small-medium-large'/> product roadmaps",
      fr: 'Alignez les collaborateurs autour de votre vision produit avec des roadmaps claires',
      de: 'Folgt einer gemeinsamen Vision <br class="hide-on-small-medium hide-on-desktop" /> mit klaren Roadmaps',
    },
    subtitle: {
      en: `Build strategic and lean roadmaps with airfocus's product roadmap software. Focus on <br class="hide-on-small-medium hide-on-desktop" /> outcomes, escape the feature factory, and share tailored roadmaps <br class="hide-on-small-medium hide-on-desktop" /> that communicate your product vision with your teams.`,
      fr: 'Élaborez vos roadmaps stratégiques avec airfocus. Concentrez-vous sur les résultats, évitez le surplus de fonctionnalités, et communiquez votre vision du produit à vos équipes en partageant vos roadmaps personnalisées.',
      de: 'Erstelle schlanke strategische Roadmaps mit der airfocus Product Roadmap Software. <br class="hide-on-small-medium hide-on-desktop" /> Konzentriere Dich auf Ergebnisse, entkomme der Feature-Hölle und teile <br class="hide-on-small-medium hide-on-desktop" /> Roadmaps, die deinem Team die Vision verdeutlichen.',
    },
    instantTourType: 'roadmaps',
    instantTourIframeSrc: 'https://capture.navattic.com/cllwcxor901ip08l35asb7efq/',
    benefits: [
      {
        title: {
          en: `Craft lean roadmaps in <br class='hide-on-small-medium-large'/> minutes with our easy-to-use roadmap builder`,
          fr: "Élaborez des roadmaps en quelques minutes grâce à notre générateur simple d'utilisation",
          de: 'Fertige in Minuten passende Roadmaps <br class="hide-on-small hide-on-large-desktop" /> mit unserem easy-to-use Roadmap Builder',
        },
        description: {
          en: 'Easily create beautiful roadmaps that always stay up to date. Visualize your roadmaps in different formats and customize their structure to fit your needs.',
          fr: 'Créez facilement vos roadmaps visuelles qui restent toujours à jour. Visualisez vos roadmaps dans différents formats et personnalisez leurs structures en fonction de vos besoins.',
          de: 'Schaffe exzellente Roadmaps, die wie von selbst aktuell bleiben. <br class="hide-on-small hide-on-large-desktop" /> Visualisiere deine Roadmaps in unterschiedlichen Formaten und <br class="hide-on-small hide-on-large-desktop" /> passe die Struktur an eure Bedürfnisse an. ',
        },
        imageSrc: 'product/board-view.webp',
        withBorder: true,
      },
      {
        title: {
          en: 'Turn strategy into action with release and timeline views',
          fr: "Passez de la stratégie à l'action grâce à nos vues chronologiques et de lancement produits",
          de: 'Mach deine Strategie zur Realität <br class="hide-on-small hide-on-large-desktop" /> mit Release und Timeline Ansichten',
        },
        description: {
          en: 'See how your strategy develops by visualizing dependencies between your items. Plan around objectives and milestones, and communicate your sprint plans easily.',
          fr: 'Découvrez comment votre stratégie peut se développer en visualisant les dépendances entre vos différents éléments. Planifiez vos objectifs et étapes clés, puis communiquez facilement vos plans de sprint.',
          de: 'Vollziehe nach, wie sich eure Strategie entwickelt, indem du Abhängigkeiten <br class="hide-on-small hide-on-large-desktop" /> zwischen den Elementen klar darstellst. Plane Ziele & Meilensteine <br class="hide-on-small hide-on-large-desktop" /> und kommuniziere euren Sprint damit kinderleicht.',
        },
        imageSrc: 'product/timeline-view.webp',
        withBorder: true,
      },
      {
        title: {
          en: 'Share tailored roadmap views with different audiences',
          fr: "Partagez des roadmaps personnalisées avec différents groupes d'utilisateurs internes ou externes",
          de: 'Teile individuelle Roadmap Ansichten <br class="hide-on-small hide-on-large-desktop" /> mit wechselnden Zielgruppen',
        },
        description: {
          en: 'Curate your roadmaps for specific audiences and seamlessly share them via secure URLs and exports.',
          fr: "Élaborez vos roadmaps pour des groupes d'utilisateurs spécifiques et partagez-les de manière transparente via des URL sécurisées ou des exports.",
          de: 'Stelle eure Roadmap zugeschnitten für eine Zielgruppe zusammen <br class="hide-on-small hide-on-large-desktop" /> und teile sie über sichere Links oder einen Export.',
        },
        imageSrc: 'ad-pages/roadmaps-views.webp',
        imgStyle: { maxWidth: '518px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 337px;
        `,
      },
      {
        title: {
          en: 'Roll up multiple roadmaps to create your own product portfolio',
          fr: 'Regroupez plusieurs roadmaps afin de créer votre propre portfolio produits complet',
          de: 'Erstelle ein Produktportfolio <br class="hide-on-small hide-on-large-desktop" /> aus mehreren Roadmaps',
        },
        description: {
          en: 'Consolidate multiple roadmaps into a single portfolio to get a better overview on the actions of multiple teams and to foster cross-functional collaboration.',
          fr: "Consolidez de multiples roadmaps en un seul portfolio afin d'obtenir une meillEure vue d'ensemble des actions de vos différentes équipes et ainsi favoriser la collaboration interfonctionnelle.",
          de: 'Fasse mehrere Roadmaps in einem einzigen Portfolio zusammen, <br class="hide-on-small hide-on-large-desktop" /> um einen besseren Überblick über die Arbeit mehrerer Teams zu erhalten <br class="hide-on-small hide-on-large-desktop" /> und die funktionsübergreifende Zusammenarbeit zu fördern.',
        },
        imageSrc: 'features/roadmaps/portfolio.webp',
        imgStyle: { maxWidth: '518px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 337px;
        `,
      },
      {
        title: {
          en: 'Visualize product work linked to your roadmap with product hierarchy',
          fr: 'Visualisez le travail produit lié à votre roadmap grâce à la hiérarchie produit',
          de: 'Visualisiere die Hierarchie deiner Roadmaps',
        },
        description: {
          en: 'Product managers prioritize on different hierarchy levels. Create your own custom workflow for each level, link hierarchies together, and keep your team aligned.',
          fr: "Les chefs de produits établissent leurs priorités à différents niveaux hiérarchiques. Créez votre propre workflow personnalisé pour chaque niveau, puis reliez les hiérarchies entre elles afin de maintenir l'alignement au sein de vos équipes.",
          de: 'Produktmanager:innen priorisieren auf verschiedenen Abstraktions- und <br class="hide-on-small hide-on-large-desktop" /> Hierarchieebenen. Erstelle passende Workflows für jedes Level, <br class="hide-on-small hide-on-large-desktop" /> verbinde die Ebenen und bleib mit dem Team auf Kurs.',
        },
        imageSrc: 'features/roadmaps/product-hierarchy.webp',
        imgStyle: { maxWidth: '528px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 350px;
        `,
      },
    ],

    ctaType: 'roadmaps',
    integrationsTitle: {
      en: 'Sync with the tools you love and save time',
      fr: 'Importez et synchronisez les retours clients <br class="hide-on-small" /> depuis vos outils existants',
      de: 'Integriere deine Tools und spare wertvolle Zeit',
    },
    integrationsDescription: {
      en: `Integrate with existing product workflows and push prioritized features to tools such as <br class='hide-on-small'/> Jira, Azure DevOps, Trello, Shortcut, and so much more.`,
      fr: "Connectez airfocus à vos autres outils tels qu'Intercom, Slack et bien d'autres <br class='hide-on-small' /> afin de recueillir et centraliser facilement les retours clients <br class='hide-on-small' /> de tous vos différents canaux.",
      de: 'Integriere die Tools, die du liebst und spare Zeit bei der Synchronisation. <br class="hide-on-small" /> Jira, Trello, Azure DevOps, Shortcut, Asana, Zapier und mehr.',
    },
    templatesTitle: {
      en: 'Discover airfocus product roadmap templates',
      fr: 'Découvrez les modèles de roadmap <br class="hide-on-small-medium hide-on-desktop" /> produit airfocus',
      de: 'Entdecke airfocus Roadmap Vorlagen',
    },
    templatesDescription: {
      en: 'Customizable templates for all your product management needs.',
      fr: 'Des modèles personnalisables selon vos besoins <br class="hide-on-small hide-on-desktop" /> en matière de gestion produits.',
      de: 'Anpassbare Vorlagen für alle eure Produktmanagement Anforderungen',
    },
    templatesList: ['outcome-based-roadmap', 'technology-roadmap', 'objective-led-roadmap'],
    testimonials: roadmapsTestimonials,
  },

  // prioritization
  {
    slug: 'prioritization',
    metaDescription: {
      en: 'Use our product prioritization tool to prioritize your roadmap and backlog around a clear product strategy with scoring frameworks, Priority Poker, and more.',
      fr: "Utilisez notre outil de priorisation produit pour organiser votre feuille de route et votre backlog autour d'une stratégie produit claire, avec des cadres de notation, Priority Poker, et plus encore.",
      de: 'Nutze unser Produkt-Priorisierungstool, um deine Roadmap und deinen Backlog gemäß einer klaren Produktstrategie mit Bewertungsrahmen, Priority Poker und mehr zu priorisieren.',
    },
    subheading: {
      en: 'Product Prioritization Tool',
      fr: 'Outil de priorisation des produits',
      de: 'Produkt Priorisierung',
    },
    heading: {
      en: `Prioritize with confidence <br class="hide-on-small hide-on-desktop" /> and solve the right problems`,
      fr: 'Priorisez en toute confiance <br class="hide-on-small hide-on-desktop" /> et résolvez les bons problèmes',
      de: 'Setze Prioritäten mit Gewissheit <br class="hide-on-small-medium hide-on-desktop" /> und löse die richtigen Probleme',
    },
    subtitle: {
      en: `Prioritize your roadmap and backlog around a clear product strategy with scoring <br class="hide-on-small hide-on-desktop" /> frameworks, Priority Poker, user insights, and OKRs. Standardize prioritization <br class="hide-on-small hide-on-desktop" /> workflows and make better product decisions as a team.`,
      fr: 'Priorisez votre roadmap et vos tickets produits existants grâce à une stratégie produit <br class="hide-on-small-medium hide-on-desktop" /> claire axée autour des cadres de notation, du Priority Poker, des informations <br class="hide-on-small-medium hide-on-desktop" /> clés de vos utilisateurs, et de vos OKRs. Optimisez la priorisation et prenez <br class="hide-on-small-medium hide-on-desktop" /> de meillEures décisions produits en équipe.',
      de: 'Priorisiere eure Roadmap und euren Backlog mit passenden Frameworks, <br class="hide-on-small hide-on-desktop" /> Priority Poker und OKRs. Standardisiere eure Abläufe und trefft <br class="hide-on-small hide-on-desktop" /> bessere Entscheidungen im Team.',
    },
    instantTourType: 'prioritization',
    instantTourIframeSrc: 'https://capture.navattic.com/clpjrnhcd01m20gl8fjmohwxl/',
    benefits: [
      {
        title: {
          en: `Prioritize on every level to<br class='hide-on-small-medium-large'/> meet your objectives`,
          fr: 'Prioriser à chaque étape pouratteindre vos objectifs',
          de: 'Priorisiere auf jeder Ebene, <br class="hide-on-small hide-on-large-desktop" /> um eure Ziele zu erreichen',
        },
        description: {
          en: 'Prioritize your strategy, discovery, and execution with built-in custom workflows. See your whole portfolio and understand the priorities of all your cross-functional teams.',
          fr: "Mettez en place les priorités de votre stratégie, votre découverte produit, et votre exécution grâce à des workflow intégrés personnalisés. Visualisez l'ensemble de votre portfolio produit et restez à jour vis à vis des priorités de vos équipes.",
          de: 'Setze Prioritäten für eure Strategie, Discovery und Ausführung mit <br class="hide-on-small hide-on-large-desktop" /> anpassbaren Workflows. Hab das ganze Portfolio im Blick und verstehe <br class="hide-on-small hide-on-large-desktop" /> die Prioritäten der cross-funktionalen Teams.',
        },
        imageSrc: 'features/prioritization/priority-score.webp',
        imgStyle: { maxWidth: '524px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 339px;
        `,
      },
      {
        title: {
          en: 'Standardize prioritization with custom scoring frameworks',
          fr: "Optimisez l'établissement des priorités grâce à des cadres de notation personnalisés",
          de: 'Standardisiere die Priorisierung <br class="hide-on-small hide-on-large-desktop" /> mit anpassbaren Frameworks',
        },
        description: {
          en: 'Use custom scoring frameworks that frame your decision-making process and enable better conversations with your teams.',
          fr: 'Mettez en place des cadres de notation personnalisés pour encadrer vos prises de décisions et améliorer les conversations entre équipes et collaborateurs.',
          de: 'Verwende benutzerdefinierte Scoring-Frameworks für euren <br class="hide-on-small hide-on-large-desktop" /> Entscheidungsprozess und gib euren Diskussionen <br class="hide-on-small hide-on-large-desktop" /> damit den richtigen Rahmen.',
        },
        imageSrc: 'product/table-view.webp',
        withBorder: true,
      },
      {
        title: {
          en: 'See the big picture and make the right product decisions',
          fr: "Obtenez une vue d'ensemble et prenez les bonnes décisions produits",
          de: 'Sie das große Ganze und <br class="hide-on-small hide-on-large-desktop" /> triff die richtigen Entscheidungen',
        },
        description: {
          en: `Visualize your strategic initiatives and opportunities, quick wins, and time-wasters to reduce the risk of business failure by seeing<br class='hide-on-small-medium-large'/> what gives you the most value.`,
          fr: "Réduisez le risque d'échec commercial: visualisez les initiatives et opportunités stratégiques apportant le plus de valeur, optimisez les améliorations rapides, et éliminez les pertes de temps inutiles.",
          de: 'Visualisiere eure strategischen Initiativen & Opportunities, eure <br class="hide-on-small hide-on-large-desktop" /> Quick Wins & Zeitfresser und reduziere das Risiko eines Misserfolgs, <br class="hide-on-small hide-on-large-desktop" /> indem du Wertvolles direkt siehst.',
        },
        imageSrc: 'features/prioritization/priority-chart.webp',
        imgStyle: { maxWidth: '526px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 330px;
        `,
      },
      {
        title: {
          en: 'Gather stakeholders to reach consensus on product priorities with Priority Poker',
          fr: 'Rassemblez les décisionnaires et obtenez un consensus sur les priorités produits grâce à la fonctionnalité Priority Poker',
          de: 'Bring alle Stakeholder zusammen <br class="hide-on-small hide-on-large-desktop" /> und finde Konsens mit Priority Poker',
        },
        description: {
          en: 'Priority Poker is an interactive and collaborative way to align around prioritization scores. Run negotiation meetings more effectively and find gaps around product decisions.',
          fr: "Établissez vos priorités de manière objective grâce à nos critères de notation, Priority Poker, et aux OKRs. Organisez vos réunions de priorisation efficacement au sein d'airfocus afin d'éviter les erreurs et mauvaises des décisions produits.",
          de: 'Mit Priority Poker stimmst du interaktiv und spielerisch Prioritäten im Team ab. <br class="hide-on-small hide-on-large-desktop" /> Sei effektiver bei den nötigen Verhandlungen und sieh die Lücken sofort.',
        },
        imageSrc: 'features/prioritization/airfocus-priority-poker.webp',
        withBorder: true,
      },
      {
        title: {
          en: 'Incorporate customer insights when prioritizing problems',
          fr: 'Incorporez les idées reçues par vos clients au moment de prioriser les problèmes produits',
          de: 'Hab die Kunden und Kundinnen <br class="hide-on-small hide-on-large-desktop" /> bei der Priorisierung im Blick',
        },
        description: {
          en: 'Identify top-requested ideas, understand what users really need, and make customer insights part of your prioritization process.',
          fr: "Identifiez les idées les plus demandées par vos clients et ce dont vos utilisateurs ont vraiment besoin, puis intégrez l'avis des clients dans votre processus de priorisation.",
          de: 'Identifiziere die meistgefragten Ideen, verstehe was Nutzende wirklich wollen <br class="hide-on-small hide-on-large-desktop" /> und lasse diese Erkenntnisse direkt in die Entwicklung einfließen.',
        },
        imageSrc: 'features/prioritization/insight-score.webp',
        imgStyle: { maxWidth: '527px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 352px;
        `,
      },
    ],
    ctaType: 'prioritization',
    integrationsTitle: {
      en: 'Sync with your favorite tools to save time',
      fr: 'Synchronisez-vous avec vos outils existants <br class="hide-on-small hide-on-desktop" /> et gagnez du temps',
      de: 'Integriere deine Tools und spare wertvolle Zeit',
    },
    integrationsDescription: {
      en: `Work closely with your cross-functional teams by integrating directly with <br class='hide-on-small'/> Jira, Azure DevOps, Trello, Shortcut, and so much more.`,
      fr: "Collaborez avec vos équipes et intégrez directement <br class='hide-on-small'/> Jira, Azure DevOps, Trello, Shortcut, et pleins d'autres outils.",
      de: 'Integriere cross-funktional mit Jira, Azure DevOps, <br class="hide-on-small"/> Trello, Shortcut und vielen anderen.',
    },
    templatesTitle: {
      en: 'Discover airfocus product prioritization templates',
      fr: 'Découvrez les modèles de priorisation <br class="hide-on-small hide-on-desktop" /> des produits airfocus',
      de: 'Entdecke unsere Priorisierungsvorlagen',
    },
    templatesDescription: {
      en: 'Customizable prioritization templates built on proven product management and decision-making methods.',
      fr: 'Des modèles de priorisation personnalisables construits au travers de méthodes <br class="hide-on-small" /> efficaces en matière de gestion produits et de prise de décision.',
      de: 'Anpassbare Priorisierungsvorlagen, die auf bewährten Produktmanagement- und Entscheidungsfindungsmethoden basieren.',
    },
    templatesList: ['value-vs-effort-prioritization', 'rice-prioritization', 'WSJF-scoring'],
    testimonials: prioritizationTestimonials,
  },

  // insights
  {
    slug: 'insights',
    metaDescription: {
      en: 'Manage feedback from all your channels and identify actionable insights with our feedback management tool.',
      fr: 'Gérez les retours de tous vos canaux et identifiez des informations exploitables avec notre outil de gestion des retours.',
      de: 'Verwalte dein Feedback aus jeglichen Kanälen und identifiziere handlungsrelevante Erkenntnisse mit unserem Feedback-Management-Tool.',
    },
    subheading: {
      en: 'Feedback management tool',
      fr: 'Outil de gestion des retours client',
      de: 'Feedback management software',
    },
    heading: {
      en: `Understand user <br class='hide-on-small-medium-large'/> needs <br class="hide-on-small hide-on-desktop" /> and solve <br class='hide-on-small-medium-large'/> the right problems`,
      fr: 'Comprenez les besoins utilisateurs et apportez une solution à leurs problèmes',
      de: 'Verstehe, was deine Kunden brauchen und löse die richtigen Probleme',
    },
    subtitle: {
      en: 'Centralize feedback from all your channels and identify actionable insights <br class="hide-on-small hide-on-desktop" /> that inform discovery and product strategy.',
      fr: 'Centralisez les retours clients provenant de tous vos canaux <br class="hide-on-small hide-on-desktop" /> et identifiez les informations exploitables favorisant la découverte produit.',
      de: 'Führe Feedback aus verschiedenen Kanälen und Teams zusammen, <br class="hide-on-small hide-on-desktop" /> um bedeutsame Erkenntnisse für die Entwicklung zu entdecken.',
    },
    benefits: [
      {
        title: {
          en: `Capture, centralize, and organize feedback all in one place`,
          fr: 'Capturez, centralisez, et organisez <br class="hide-on-small hide-on-large-desktop" /> les retours clients en un seul endroit',
          de: 'Erfasse, zentralisiere und organisiere <br class="hide-on-small hide-on-large-desktop" /> Feedback an einem Ort',
        },
        description: {
          en: 'Ensure no information gets lost by collating feedback from email, chat, support, and other channels in one organized inbox.',
          fr: "Assurez-vous qu'aucune information ne se perde en recueillant <br class='hide-on-small hide-on-large-desktop' /> les avis provenant des emails, du chat, du support client <br class='hide-on-small hide-on-large-desktop' /> et d'autres canaux au sein d'une boîte de réception centralisée.",
          de: 'Verliere keine Informationen, indem du Feedback aus Emails, Chats, <br class="hide-on-small hide-on-large-desktop" /> Supportnachrichten und weiteren Kanälen in einer Inbox sammelst.',
        },
        imageSrc: 'product/inbox-view.webp',
        withBorder: true,
      },
      {
        title: {
          en: 'Turn valuable feedback into insights and inform product discovery',
          fr: "Transformez les retours clients précieux <br class='hide-on-small hide-on-large-desktop' /> en idées et optimisez la recherche produit",
          de: 'Verwandle wertvolles Feedback <br class="hide-on-small hide-on-large-desktop" /> in Erkenntnisse für die Entwicklung',
        },
        description: {
          en: 'Generate insights by linking customer feedback to ideas, opportunities, or features in your backlog.',
          fr: "Générez plus d'informations en associant les avis clients <br class='hide-on-small hide-on-large-desktop' /> à vos idées, vos opportunités, ou aux fonctionnalités à venir.",
          de: 'Finde neue Erkenntnisse, wenn du Feedback direkt mit Ideen, <br class="hide-on-small hide-on-large-desktop" /> Opportunities oder Features in deinem Backlog verknüpfst.',
        },
        imageSrc: 'features/insights/insights.webp',
        imgStyle: { maxWidth: '526px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 343px;
        `,
      },
      {
        title: {
          en: `Include customer insights<br class='hide-on-small-medium-large'/>  when prioritizing problems`,
          fr: 'Prenez en compte les avis clients <br class="hide-on-small hide-on-large-desktop" /> au moment de la priorisations des problèmes',
          de: 'Berücksichtige deine Kunden <br class="hide-on-small hide-on-large-desktop" /> und Kundinnen bei der Priorisierung',
        },
        description: {
          en: `Surface frequently-requested ideas, understand the “why” of your customers' needs, and make user insights part of your prioritization framework.`,
          fr: "Faites remonter les idées fréquemment demandées par vos clients, <br class='hide-on-small hide-on-large-desktop' /> comprenez les motivations derrière leurs besoins, et intégrez leurs <br class='hide-on-small hide-on-large-desktop' /> avis au sein de votre priorisation produit.",
          de: 'Mache häufig nachgefragte Ideen sichtbar, verstehe das "Warum" <br class="hide-on-small hide-on-large-desktop" /> der Bedürfnisse deiner Kunden und Kundinnen und integriere <br class="hide-on-small hide-on-large-desktop" /> diese Erkenntnisse bei der Priorisierung.',
        },
        imageSrc: 'product/table-view.webp',
        withBorder: true,
      },
      {
        title: {
          en: 'Manage feedback with your own set of workflows',
          fr: 'Gérez les retours clients <br class="hide-on-small hide-on-large-desktop" /> au sein de vos propres méthodologies',
          de: 'Verwalte dein Feedback <br class="hide-on-small hide-on-large" /> mit passenden Workflows',
        },
        description: {
          en: 'Segment, organize, and visualize feedback in whatever way that makes sense for you. Never miss out on user needs and discover new opportunities along the way.',
          fr: 'Segmentez, organisez, et visualisez les retours clients de la manière <br class="hide-on-small hide-on-large-desktop" /> qui vous convient le mieux. Ne passez jamais à côté des besoins <br class="hide-on-small hide-on-large-desktop" /> utilisateurs et découvrez de nouvelles opportunités en cours de route.',
          de: 'Segmentiere, organisiere und visualisiere dein Feedback so, dass es <br class="hide-on-small hide-on-large-desktop" /> für Dich Sinn ergibt. Übersieh keine Kundenwünsche und <br class="hide-on-small hide-on-large-desktop" /> entdecke auf dem Weg neue Möglichkeiten.',
        },
        imageSrc: 'features/insights/airfocus-insight-tableview.webp',
        withBorder: true,
      },
      {
        title: {
          en: 'Collect feedback with a branded portal',
          fr: "Recueillez des avis clients <br class='hide-on-small hide-on-large-desktop' /> au sein d'un portail personnalisé <br class='hide-on-small hide-on-large-desktop' /> aux couleurs de votre entreprise",
          de: 'Sammel Feedback <br class="hide-on-small-medium" /> über das Portal ',
        },
        description: {
          en: `Engage with your community on a<br class='hide-on-small-medium-large'/>  customer-facing website by sharing your roadmaps and ideas, gathering feedback, and validating concepts with them.`,
          fr: 'Engagez votre communauté grâce à un micro-site client permettant <br class="hide-on-small hide-on-large-desktop" /> de partager votre roadmap et vos idées, de recueillir <br class="hide-on-small hide-on-large-desktop" /> les avis clients, et de valider vos futurs concepts <br class="hide-on-small hide-on-large-desktop" /> avec vos utilisateurs.',
          de: 'Sei über das Portal im Kontakt mit deiner Community. Teile deine Roadmaps <br class="hide-on-small hide-on-large-desktop" /> und Ideen, sammele Feedback und validiere deine Konzepte.',
        },
        imageSrc: 'features/portal/airfocus-portal.webp',
        withBorder: true,
      },
      {
        title: {
          en: `Close the customer <br class='hide-on-small-medium-large'/> feedback loop`,
          fr: 'Gardez vos clients informés <br class="hide-on-small hide-on-large-desktop" /> des évolutions produit',
          de: 'Schließe den Feedback-Loop',
        },
        description: {
          en: 'Build transparency by informing and updating your customers about your product and their needs. Be the superhero they always wanted.',
          fr: "Informez vos clients de vos évolutions produits et comprenez leurs besoins. <br class='hide-on-small hide-on-large-desktop' /> Devenez le super-héros qu'ils ont toujours voulu avoir à leurs côtés.",
          de: 'Schaffe Transparenz, indem du eure Kunden und Kundinnen zu neuen <br class="hide-on-small hide-on-large-desktop" /> Entwicklungen und ihren Bedürfnissen auf dem Laufenden hältst. <br class="hide-on-small hide-on-large-desktop" /> Sei der Superstar, den sie sich wünschen.',
        },
        imageSrc: 'features/insights/feedback-loop.webp',
        imgStyle: { maxWidth: '526px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 345px;
        `,
      },
    ],
    ctaType: 'insights',
    integrationsTitle: {
      en: 'Import and sync feedback from all your tools',
      fr: 'Importez et synchronisez les retours clients <br class="hide-on-small" /> depuis vos outils existants',
      de: 'Importiere und synchronisiere Feedback <br class="hide-on-small" /> aus all euren Tools',
    },
    integrationsDescription: {
      en: `Connect airfocus with tools like Intercom, Slack, and more to easily gather and centralize feedback from various channels.`,
      fr: "Connectez airfocus à vos autres outils tels qu'Intercom, Slack et bien d'autres <br class='hide-on-small' /> afin de recueillir et centraliser facilement les retours clients <br class='hide-on-small' /> de tous vos différents canaux.",
      de: "Verbinde airfocus mit Intercom, Slack und anderen, um dein Feedback <br class='hide-on-small' /> an einem Ort zu sammeln und zu verarbeiten.",
    },
    templatesTitle: {
      en: 'Discover feedback management templates',
      fr: 'Découvrez nos modèles de gestion <br class="hide-on-small-medium" /> des retours clients',
      de: 'Entdecke Feedback Vorlagen',
    },
    templatesDescription: {
      en: 'Customizable templates to better manage feedback and inform product discovery.',
      fr: "Des modèles personnalisables afin de mieux gérer les retours <br class='hide-on-small-medium' /> d'expérience clients et faciliter la découverte produit.",
      de: 'Anpassbare Vorlagen zur besseren Verwaltung von Feedback und zur Product Discovery.',
    },
    templatesList: ['feedback-and-insights', 'full-product-management-workflow', 'product-management'],
    testimonials: insightsTestimonials,
  },

  // portal
  {
    slug: 'portal',
    metaDescription: {
      en: 'Involve your audiences on a branded portal by sharing your roadmaps, plans, and releases. Collect feedback with ease and validate new ideas.',
      fr: 'Impliquez votre public sur un portail personnalisé en partageant vos feuilles de route, plans et publications. Collectez des retours facilement et validez de nouvelles idées.',
      de: 'Binde deinen Stakeholdern mit einem anpassbaren Portal ein, indem du deine Roadmaps, Pläne und Veröffentlichungen teilst. Sammle Feedback mühelos und validiere neue Ideen.',
    },
    subheading: {
      en: 'Customer Engagement Software',
      fr: "Logiciel d'engagement client",
      de: 'Customer Engagement Software',
    },
    heading: {
      en: 'Engage with your community <br class="hide-on-small-medium hide-on-desktop" /> on a branded portal',
      fr: "Engagez votre communauté d'utilisateurs sur un portail à votre image",
      de: 'Sei mit einem Portal in Kontakt <br class="hide-on-small-medium hide-on-desktop" /> mit eurer Community',
    },
    subtitle: {
      en: 'Involve your audiences on a branded portal by sharing your roadmaps, plans, <br class="hide-on-small hide-on-desktop" /> and releases. Collect feedback with ease and validate new ideas.',
      fr: "Partagez vos roadmaps, vos plans, et vos lancements produits, et impliquez <br class='hide-on-small hide-on-desktop' /> vos utilisateurs au sein d'un portail personnalisé. Recueillez facilement <br class='hide-on-small hide-on-desktop' /> les avis et validez les nouvelles idées.",
      de: 'Teile eure Roadmap, Pläne und Releases mit deinen Stakeholdern <br class="hide-on-small hide-on-desktop" /> auf einem anpassbaren Portal. Sammel dabei Feedback ein <br class="hide-on-small hide-on-desktop" /> und validiere Ideen und Konzepte.',
    },
    benefits: [
      {
        title: {
          en: 'Create a portal that matches your brand',
          fr: "Créez un portail <br class='hide-on-small hide-on-large-desktop'/> à l'image de votre entreprise",
          de: 'Erstelle ein Portal, das zu eurer Marke passt',
        },
        description: {
          en: 'Build a branded customer-facing website where you can share product work and ideas with your audiences.',
          fr: 'Créez un micro-site personnalisable, visualisable par vos clients, <br class="hide-on-small- hide-on-large-desktop"/> où vous pourrez partager vos travaux et vos idées avec vos utilisateurs.',
          de: 'Gestalte eine markengerechte Seite für eure Kunden und Kundinnen, <br class="hide-on-small- hide-on-large-desktop"/> auf der du eure Arbeit und Ideen mit ihnen teilen kannst. ',
        },
        imageSrc: 'features/portal/airfocus-portal.webp',
        withBorder: true,
      },
      {
        title: {
          en: 'Share roadmaps and ideas, <br class="hide-on-small- hide-on-large-desktop"/> and celebrate recent launches',
          fr: 'Partagez vos roadmaps et vos idées, <br class="hide-on-small- hide-on-large-desktop"/> et célébrez les lancements réussis',
          de: 'Teile Roadmaps & Ideen <br class="hide-on-small- hide-on-large-desktop"/> und feiere eure neuesten Releases',
        },
        description: {
          en: `Keep your customers in the loop by sharing what's been launched and what's coming up. Engage with them on public product roadmaps, share discovery ideas, and announce releases.`,
          fr: 'Gardez vos clients à jour en partageant ce qui a été lancé récemment, <br class="hide-on-small- hide-on-large-desktop"/> et ce qui est à venir au sein de votre produit. Échangez avec eux <br class="hide-on-small- hide-on-large-desktop"/> sur vos roadmaps produits publiques, partagez leurs idées en interne, <br class="hide-on-small- hide-on-large-desktop"/> et communiquez avec eux lors de vos nouveaux lancements.',
          de: 'Halte eure Kunden und Kundinnen über eure neuesten Entwicklungen <br class="hide-on-small- hide-on-large-desktop"/> auf dem Laufenden. Lass sie wissen, was kommen wird <br class="hide-on-small- hide-on-large-desktop"/> und binde sie in eure Überlegungen mit ein.',
        },
        imageSrc: 'features/portal/post-to-portal.webp',
        imgStyle: { maxWidth: '578px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 364px;
        `,
      },
      {
        title: {
          en: `Gather feedback and new <br class='hide-on-small-medium-large'/> ideas with ease`,
          fr: 'Capturez facilement les retours clients <br class="hide-on-small- hide-on-large-desktop"/> et les nouvelles idées',
          de: 'Sammel ohne Mühe Feedback und Ideen ein',
        },
        description: {
          en: `Bridge the gap between your product team and your audience. Let your community's voices be heard by allowing them to submit feedback and product ideas easily.`,
          fr: 'Rapprochez vos équipes produits de vos utilisateurs. Faites remonter <br class="hide-on-small- hide-on-large-desktop"/> la voix de votre communauté en leur permettant de partager <br class="hide-on-small- hide-on-large-desktop"/> leurs avis et idées produits facilement.',
          de: 'Überbrücke den Graben zwischen Eurem Produktteam und den Nutzenden. <br class="hide-on-small- hide-on-large-desktop"/> Gib eurer Community eine Stimme und lass sie schnell und <br class="hide-on-small- hide-on-large-desktop"/> einfach Feedback geben.',
        },
        imageSrc: 'features/portal/feedback.webp',
        imgStyle: { maxWidth: '483px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 440px;
        `,
      },
      {
        title: {
          en: `Test and validate ideas <br class='hide-on-small-medium-large'/> with the crowd`,
          fr: 'Testez et validez <br class="hide-on-small- hide-on-large-desktop"/> vos idées avec vos clients',
          de: 'Teste Ideen mit deiner Community',
        },
        description: {
          en: `Run experiments, evaluate your ideas, and <br class='hide-on-small-medium-large'/>  improve your discovery process by inviting users to continuously provide feedback.`,
          fr: 'Réalisez des études clients, évaluez vos idées, et améliorez votre <br class="hide-on-small- hide-on-large-desktop"/> processus de découverte produit en invitant vos utilisateurs à vous <br class="hide-on-small- hide-on-large-desktop"/> faire part de leurs avis en continu.',
          de: 'Führe Experimente durch, evaluiere Ideen und verbessere eure Product <br class="hide-on-small- hide-on-large-desktop"/> Discovery, indem du Nutzende kontinuierlich zu Feedback einlädst.',
        },
        imageSrc: 'features/portal/test-and-validate.webp',
        imgStyle: { maxWidth: '524px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 330px;
        `,
      },
      {
        title: {
          en: 'Turn portal feedback into actionable insights',
          fr: 'Transformez les avis du portail <br class="hide-on-small- hide-on-large-desktop"/> en informations exploitables par vos équipes',
          de: 'Verwandel Feedback über das Portal <br class="hide-on-small- hide-on-large-desktop"/> in neue Erkenntnisse',
        },
        description: {
          en: 'Centralize portal feedback and generate insights to better understand your audience. Close the feedback loop by keeping your customers up to date on your product and their requests.',
          fr: "Centralisez les avis au sein du portail airfocus et générez <br class='hide-on-small hide-on-large-desktop'/> des informations afin de mieux comprendre les attentes de vos utilisateurs. <br class='hide-on-small hide-on-large-desktop'/> Fermez la boucle des retours d'informations en tenant vos clients informés <br class='hide-on-small hide-on-large-desktop'/> de l'évolution de leurs demandes et de vos produits.",
          de: 'Sammel dank des Portals mehr Feedback an einem Ort und verstehe so <br class="hide-on-small- hide-on-large-desktop"/> deine Zielgruppe besser. Schließe Feedback-Lücken und lass deine <br class="hide-on-small- hide-on-large-desktop"/> Nutzenden über den aktuellen Stand im Bilde bleiben.',
        },
        imageSrc: 'features/insights/insights.webp',
        imgStyle: { maxWidth: '526px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 344px;
        `,
      },
      {
        title: {
          en: 'Quickly collect votes from stakeholders',
          fr: 'Recueillez rapidement <br class="hide-on-small- hide-on-large-desktop"/> les votes des décisionnaires',
          de: 'Sammel mit Abstimmungen <br class="hide-on-small- hide-on-large-desktop"/> schnelle Meinungsbilder ein',
        },
        description: {
          en: "Gauge an item's demand by collecting votes or reactions through your Portal. Pick the voting method that suits your needs and get back to your audience about their opinion.",
          fr: "Évaluez la demande d'une fonctionnalité en recueillant des votes et réactions <br class='hide-on-small hide-on-large-desktop'/> par l'intermédiaire de votre portail. Choisissez la méthode de vote qui <br class='hide-on-small hide-on-large-desktop'/> vous convient et partagez vos avancées avec vos utilisateurs.",
          de: 'Ermittle mit Abstimmungen auf Eurem Portal schnell die Nachfrage <br class="hide-on-small- hide-on-large-desktop"/> nach einem Element. Wähle eine der drei Abstimmungsmethoden <br class="hide-on-small- hide-on-large-desktop"/> aus, die zu euch passt und hol Dir eine Meinung ein.',
        },
        imageSrc: 'features/portal/voting.webp',
        imgStyle: { maxWidth: '500px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 370px;
        `,
      },
    ],
    ctaType: 'general',
    integrationsTitle: {
      en: 'Sync with the tools you love and save time',
      fr: 'Importez et synchronisez les retours clients <br class="hide-on-small" /> depuis vos outils existants',
      de: 'Integriere deine Tools und spare wertvolle Zeit',
    },
    integrationsDescription: {
      en: `Integrate with existing product workflows and push prioritized features to tools such as<br class='hide-on-small'/> Jira, Azure DevOps, Trello, Shortcut, and so much more.`,
      fr: "Connectez airfocus à vos autres outils tels qu'Intercom, Slack et bien d'autres <br class='hide-on-small' /> afin de recueillir et centraliser facilement les retours clients <br class='hide-on-small' /> de tous vos différents canaux.",
      de: 'Integriere die Tools, die du liebst und spare Zeit bei der Synchronisation. <br class="hide-on-small" /> Jira, Trello, Azure DevOps, Shortcut, Asana, Zapier und mehr.',
    },
    templatesTitle: {
      en: 'Discover product management templates',
      fr: 'Découvrez les modèles de gestion produits',
      de: 'Entdecke Vorlagen fürs Produktmanagement',
    },
    templatesDescription: {
      en: 'Customizable templates to better manage feedback and inform product discovery.',
      fr: 'Des modèles personnalisables pour mieux gérer les retours clients et éclairer la découverte produit.',
      de: 'Anpassbare Vorlagen zur besseren Erfassung von Feedback für die Product Discovery.',
    },
    templatesList: ['feedback-and-insights', 'full-product-management-workflow', 'product-management'],
    testimonials: portalTestimonials,
  },

  // objectives & okrs
  {
    slug: 'objectives-okrs',
    metaDescription: {
      en: 'Elevate your product strategy with our Objectives Management and OKR Software for Product Management Teams. Ditch the spreadsheets, align your team, and track progress from user stories to big-picture goals—all in one platform.',
      fr: 'Rehaussez votre stratégie produit avec notre logiciel de gestion des objectifs et des OKR dédié aux équipes de gestion produit. Abandonnez les tableurs, alignez votre équipe et suivez la progression, des user stories aux objectifs globaux, le tout sur une seule plateforme.',
      de: 'Bring deine Produktstrategie mit unserer Objectives Management und OKR Software für Produktmanagement-Teams auf ein neues Level. Verabschiede Dich von Tabellenkalkulationen, stimme dein Team ab und verfolge den Fortschritt von Benutzerstories bis hin zu großen Zielen - alles auf einer Plattform.',
    },
    subheading: {
      en: 'OKR software for product teams',
      fr: 'Logiciel OKR pour les équipes produit',
      de: 'OKR Software für Produktteams',
    },
    heading: {
      en: `Supercharge your product strategy <br class="hide-on-small-medium hide-on-desktop" /> with built-in OKRs`,
      fr: "Optimisez votre stratégie produit grâce à l'intégration de vos OKRs",
      de: 'Optimiere eure Produktstrategie <br class="hide-on-small-medium hide-on-desktop" /> mit integrierten OKRs',
    },
    subtitle: {
      en: `Keep your OKRs, roadmaps, and product workflows interconnected to drive team alignment. Say goodbye to spreadsheets and disconnected strategy. Track multiple <br class="hide-on-small-medium hide-on-desktop" /> teams' progress, from user stories to company-wide <br class="hide-on-small-medium hide-on-desktop" /> objectives — all in one place.`,
      fr: "Connectez vos OKRs, roadmaps, et workflow pour aligner les équipes. <br class='hide-on-small-medium hide-on-desktop' /> Fini les décalages avec des feuilles de calcul. Suivez les progrès, <br class='hide-on-small-medium hide-on-desktop' /> des scénarios utilisateurs aux objectifs d'entreprise, <br class='hide-on-small-medium hide-on-desktop' /> en un seul outil central.",
      de: 'Verknüpfe OKRs, Roadmaps und Arbeitsabläufe und richte dein Team auf ein klares Ziel aus. Wink deinen Spreadsheets und einer kontext-losen Strategie noch kurz zum Abschied, bevor du der Komplettansicht zum Fortschritt aller Teams von der einzelnen Story bis zu den Unternehmenszielen Hallo sagst.',
    },
    instantTourType: 'objectives-okrs',
    instantTourIframeSrc: 'https://capture.navattic.com/clsbvxw18005u0fl4cnwsfs0f/',
    benefits: [
      {
        title: {
          en: 'Elevate outcomes with OKRs built into your PM platform',
          fr: 'Optimisez vos résultats avec les OKRs <br class="hide-on-small hide-on-large-desktop" /> dans votre plateforme de projets',
          de: 'Hebe deine Ergebnisse mit <br class="hide-on-small hide-on-large-desktop" /> integrierten OKRs auf das nächste Level',
        },
        description: {
          en: "Manage your OKRs in airfocus and connect them directly to your roadmaps and workflows. Get all your product artifacts in one place to understand your teams' progress across all levels - from a single user story all the way up to your long-term company objectives.",
          fr: 'Gérez vos OKRs dans airfocus, reliez-les directement à vos roadmaps <br class="hide-on-small hide-on-large-desktop" /> et travail produit. Centralisez tous les éléments pour suivre le progrès <br class="hide-on-small hide-on-large-desktop" /> de vos équipes à tous les niveaux, du scénario utilisateur <br class="hide-on-small hide-on-large-desktop" /> aux objectifs à long terme.',
          de: 'Verwalte deine OKRs in airfocus und verbinde sie direkt mit deiner Roadmap. <br class="hide-on-small hide-on-large-desktop" /> Halte alle Abläufe und Artefakte an einem Ort zusammen und verstehe so <br class="hide-on-small hide-on-large-desktop" /> den Fortschritt des Teams auf jedem Level. Von der einzelnen User story <br class="hide-on-small hide-on-large-desktop" /> bis zu den langfristigen Unternehmenszielen.',
        },
        videoFileName: 'okr-outcome',
      },
      {
        title: {
          en: 'Link product work to OKRs to not lose sight of your strategy',
          fr: 'Reliez votre travail produit à vos OKRs afin <br class="hide-on-small hide-on-large-desktop" /> de ne jamais perdre de vue votre stratégie initiale',
          de: 'Verknüpfe die Arbeit am Produkt mit OKRs <br class="hide-on-small hide-on-large-desktop" /> und habe damit das Wichtigste immer im Blick',
        },
        description: {
          en: 'Link your airfocus items, such as initiatives, epics, or tasks, to key results to ensure the day-to-day product work remains on track with your overarching objectives.',
          fr: 'Liez vos éléments airfocus (tels que les initiatives, les epics, ou les tâches) <br class="hide-on-small hide-on-large-desktop" /> à vos OKRs afin de vous assurer que votre travail produit au quotidien <br class="hide-on-small hide-on-large-desktop" /> reste en phase avec vos objectifs globaux.',
          de: 'Veknüpfe eure airfocus Elemente, wie Initiativen, Epics oder Tasks, <br class="hide-on-small hide-on-large-desktop" /> mit Key Results, damit eure tägliche Arbeit mit den <br class="hide-on-small hide-on-large-desktop" /> übergeordneten Zielen im Einklang bleibt.',
        },
        imageSrc: 'features/objectives/link-okrs.webp',
        videoFileName: 'okr-linking',
      },
      {
        title: {
          en: 'Automatically track progress and adapt fast',
          fr: "Suivre automatiquement les progrès <br class='hide-on-small hide-on-large-desktop' /> et s'adapter rapidement",
          de: 'Erfasse Fortschritt automatisch <br class="hide-on-small hide-on-large-desktop" /> und nimm schnell die richtigen Anpassungen vor',
        },
        description: {
          en: 'Submit regular check-ins to get real-time context of your progress, helping you spot at-risk objectives and make timely adjustments.',
          fr: 'Obtenez des informations en temps réel sur les progrès effectués, <br class="hide-on-small hide-on-large-desktop" /> identifiez vos objectifs à risque, et ajustez votre stratégie au bon moment.',
          de: 'Bleib mit regelmäßigen Check-Ins auf dem Laufenden: aktualisiere, <br class="hide-on-small hide-on-large-desktop" /> erfasse und identifiziere gefährdete Objectives. Mache so <br class="hide-on-small hide-on-large-desktop" /> rechtzeitige Anpassungen, wo selbige nötig sind.',
        },
        videoFileName: 'okr-progress',
      },
      {
        title: {
          en: 'Empower through transparency',
          fr: 'Responsabiliser grâce à la transparence',
          de: 'Handlungskomptenz durch Transparenz',
        },
        description: {
          en: "Make OKRs accessible to everyone on your team. Give individual contributors a clear line of sight into how they're driving company success.",
          fr: "Rendez les OKRs transparents et accessibles à tous les membres <br class='hide-on-small hide-on-large-desktop' /> de votre équipe. Donnez aux collaborateurs une vision claire <br class='hide-on-small hide-on-large-desktop' /> de la manière dont ils contribuent à la réussite de l'entreprise.",
          de: 'Mache die OKRs für alle Teammitglieder zugänglich. Gib einzelnen <br class="hide-on-small hide-on-large-desktop" /> Mitarbeitenden einen klaren Überblick, wie sie zum <br class="hide-on-small hide-on-large-desktop" /> Unternehmenserfolg beitragen.',
        },
        videoFileName: 'okr-transparency',
      },
      {
        title: {
          en: 'Implement any objective management framework',
          fr: 'Mettre en œuvre votre propre cadre <br class="hide-on-small hide-on-large-desktop" /> de gestion des objectifs',
          de: 'Nutze ein Objectives Framework deiner Wahl',
        },
        description: {
          en: "Track your goals and progress regardless of what process or framework you use. Benefit from airfocus's flexibility by customizing objective hierarchies, time periods, views, and fields to reflect how you plan and work.",
          fr: "Suivez l'avancée de vos objectifs et de votre progrès quel que soit <br class='hide-on-small hide-on-large-desktop' /> le processus mis en place. Profitez de la flexibilité d'airfocus, et personnalisez <br class='hide-on-small hide-on-large-desktop' /> les hiérarchies d'objectifs, les périodes, les vues et les champs à remplir, <br class='hide-on-small hide-on-large-desktop' /> afin de refléter votre façon unique de planifier et de travailler.",
          de: "Verfolge euren Fortschritt unabhängig vom genutzten Framework. <br class='hide-on-small hide-on-large-desktop' /> Mache Dir airfocus' Flexibilität zu eigen und passe die Hierachie, <br class='hide-on-small hide-on-large-desktop' /> Zeitabstände, Ansichten und Felder so an, dass sie <br class='hide-on-small hide-on-large-desktop' /> eure Arbeitsweise reflektieren.",
        },
        videoFileName: 'okr-framework',
        withBorder: true,
      },
    ],
    ctaType: 'objectives',
    // following sections will not be shown on Objectives & OKRs page
    integrationsTitle: { en: '', fr: '', de: '' },
    integrationsDescription: { en: '', fr: '', de: '' },
    templatesTitle: { en: '', fr: '', de: '' },
    templatesDescription: { en: '', fr: '', de: '' },
    templatesList: '',
    testimonials: roadmapsTestimonials,
  },
  {
    slug: 'capacity-planning',
    metaDescription: {
      en: 'Easily manage high-level capacity planning across teams by setting capacity limits for each planning segment, such as quarters or releases. Align resource demands with capacity and prioritize work that drives the most business value.',
      fr: '',
      de: '',
    },
    subheading: {
      en: 'Capacity planning tool',
      fr: '',
      de: '',
    },
    heading: {
      en: 'Simple resource management <br class="hide-on-small-medium hide-on-desktop" /> with Capacity Planning',
      fr: '',
      de: '',
    },
    subtitle: {
      en: 'Easily manage high-level capacity planning across teams by setting capacity <br class="hide-on-small hide-on-large-desktop" /> limits for <br class="hide-on-small-medium hide-on-desktop" /> each planning segment, such as quarters or releases. Align <br class="hide-on-small hide-on-large-desktop" /> resource demands with <br class="hide-on-small-medium hide-on-desktop" /> capacity and prioritize work that drives <br class="hide-on-small hide-on-large-desktop" /> the most business value.',
      fr: '',
      de: '',
    },
    benefits: [
      {
        title: {
          en: 'Simple capacity management',
          fr: '',
          de: '',
        },
        description: {
          en: 'Manage resource allocation across planning segments (e.g., quarters, releases) <br class="hide-on-small hide-on-large-desktop" /> in one view, even as plans shift or priorities change mid-flight.',
          fr: '',
          de: '',
        },
        imageSrc: 'features/capacity-planning/simple-capacity.webp',
        imgStyle: { maxWidth: '483px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 301px;
        `,
      },
      {
        title: {
          en: 'Understand trade-offs with scenario planning',
          fr: '',
          de: '',
        },
        description: {
          en: 'Simulate and compare scenarios to understand trade-offs, helping <br class="hide-on-small hide-on-large-desktop" /> you make decisions based on actual capacity.',
          fr: '',
          de: '',
        },
        imageSrc: 'features/capacity-planning/trade-offs.webp',
        imgStyle: { maxWidth: '483px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 305px;
        `,
      },
      {
        title: {
          en: `Prioritize with the big picture in mind`,
          fr: '',
          de: '',
        },
        description: {
          en: 'Prioritization comes down to capacity. Make sure your plans fit available <br class="hide-on-small hide-on-large-desktop" /> resources across teams and workspaces, keeping <br class="hide-on-small hide-on-large-desktop" /> efforts aligned and efficient.',
          fr: '',
          de: '',
        },
        imageSrc: 'features/capacity-planning/big-picture.webp',
        imgStyle: { maxWidth: '483px !important', height: 'auto', margin: '0 auto 0' },
        imageCss: css`
          height: 330px;
        `,
      },
    ],
    ctaType: 'capacity-planning',
    // following sections will not be shown on Capacity Planning page
    integrationsTitle: {
      en: '',
      fr: '',
      de: '',
    },
    integrationsDescription: {
      en: '',
      fr: '',
      de: '',
    },
    templatesTitle: {
      en: '',
      fr: '',
      de: '',
    },
    templatesDescription: {
      en: '',
      fr: '',
      de: '',
    },
    templatesList: '',
    testimonials: roadmapsTestimonials,
  },
]

export const FEATURES_CARDS = [
  {
    title: { en: 'Roadmaps', fr: 'Roadmaps', de: 'Roadmaps' },
    description: '',
    link: { en: '/product/roadmaps/', fr: '/fr/product/roadmaps/', de: '/de/product/roadmaps/' },
    imageSrc: 'features/roadmaps.webp',
  },
  {
    title: { en: 'Prioritization', fr: 'Priorisation', de: 'Priorisierung' },
    description: '',
    link: { en: '/product/prioritization/', fr: '/fr/product/prioritization/', de: '/de/product/prioritization/' },
    imageSrc: 'features/prioritization.webp',
  },
  {
    title: { en: 'Feedback & Insights', fr: 'Gestion des retours utilisateurs', de: 'Feedback & Erkenntnisse' },
    description: '',
    link: { en: '/product/insights/', fr: '/fr/product/insights/', de: '/de/product/insights/' },
    imageSrc: 'features/insights.webp',
  },
  {
    title: { en: 'Objectives & OKRs', fr: 'Objectifs & OKRs', de: 'Objectives & OKRs' },
    description: '',
    link: { en: '/product/objectives-okrs/', fr: '/fr/product/objectives-okrs/', de: '/de/product/objectives-okrs/' },
    imageSrc: 'features/objectives.webp',
  },
  {
    title: { en: 'Capacity Planning', fr: 'Allocation des ressources', de: '' },
    description: '',
    link: { en: '/product/capacity-planning/', fr: '', de: '' },
    imageSrc: 'features/capacity-planning.webp',
  },
  {
    title: { en: 'Portal', fr: 'Portail', de: 'Portal' },
    description: '',
    link: { en: '/product/portal/', fr: '/fr/product/portal/', de: '/de/product/portal/' },
    imageSrc: 'features/portal.webp',
  },
]
